import { DfrData } from '../types';
import { AIAG_VDA_STEPS, AIAGVDAStep } from '../constants/aiagVDASteps';
import { calculateActionPriority, calculateRPN } from './riskCalculators';

export interface ComplianceMetrics {
    overallCompliance: number; // 0-100%
    stepCompliance: {
        stepNumber: number;
        stepName: string;
        compliance: number; // 0-100%
        missingFields: string[];
        itemCount: number;
        compliantCount: number;
    }[];
    criticalIssues: {
        severity: 'critical' | 'high' | 'medium';
        message: string;
        step: number;
        affectedItems: string[];
    }[];
    recommendations: string[];
    stats: {
        totalFmeaItems: number;
        highRiskCount: number; // RPN >= 100 or AP === 'H'
        itemsWithoutControls: number;
        itemsWithoutTests: number;
        completePDiagrams: number;
        totalPDiagrams: number;
    };
}

/**
 * Calculate AIAG-VDA compliance metrics locally (no API calls)
 * This provides instant feedback on workflow progress
 */
export const calculateAIAGVDACompliance = (data: DfrData): ComplianceMetrics => {
    const stepCompliance = AIAG_VDA_STEPS.map(step => analyzeStep(step, data));
    const overallCompliance = Math.round(
        stepCompliance.reduce((sum, s) => sum + s.compliance, 0) / AIAG_VDA_STEPS.length
    );

    const criticalIssues = identifyCriticalIssues(data, stepCompliance);
    const recommendations = generateRecommendations(data, stepCompliance);
    const stats = calculateStats(data);

    return {
        overallCompliance,
        stepCompliance,
        criticalIssues,
        recommendations,
        stats,
    };
};

/**
 * Analyze compliance for a single AIAG-VDA step
 */
const analyzeStep = (step: AIAGVDAStep, data: DfrData) => {
    const missingFields: string[] = [];
    let compliantCount = 0;
    let itemCount = 0;

    // Check FMEA fields
    if (step.requiredFields.fmea) {
        itemCount = data.fmeaItems.length;
        data.fmeaItems.forEach(item => {
            const isCompliant = step.requiredFields.fmea!.every(field => {
                const value = (item as any)[field];
                if (Array.isArray(value)) return value.length > 0;
                return value !== undefined && value !== null && value !== '';
            });
            if (isCompliant) compliantCount++;
            else {
                step.requiredFields.fmea!.forEach(field => {
                    const value = (item as any)[field];
                    const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
                    if (isEmpty && !missingFields.includes(field)) {
                        missingFields.push(field);
                    }
                });
            }
        });
    }

    // Check P-Diagram fields
    if (step.requiredFields.pDiagram) {
        const pDiagramCount = data.pDiagramList.length;
        if (itemCount === 0) itemCount = pDiagramCount;

        data.pDiagramList.forEach(pd => {
            const isCompliant = step.requiredFields.pDiagram!.every(field => {
                const value = (pd as any)[field];
                if (Array.isArray(value)) return value.length > 0;
                return value !== undefined && value !== null && value !== '';
            });
            if (isCompliant) compliantCount++;
            else {
                step.requiredFields.pDiagram!.forEach(field => {
                    const value = (pd as any)[field];
                    const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
                    if (isEmpty && !missingFields.includes(`pDiagram.${field}`)) {
                        missingFields.push(`pDiagram.${field}`);
                    }
                });
            }
        });
    }

    // Check Controls
    if (step.requiredFields.controls) {
        const controlCount = data.controls.length;
        if (itemCount === 0) itemCount = controlCount;

        data.controls.forEach(ctrl => {
            const isCompliant = step.requiredFields.controls!.every(field => {
                const value = (ctrl as any)[field];
                return value !== undefined && value !== null && value !== '';
            });
            if (isCompliant) compliantCount++;
        });
    }

    // Check Tests
    if (step.requiredFields.tests) {
        const testCount = data.dvprTests.length;
        if (itemCount === 0) itemCount = testCount;

        data.dvprTests.forEach(test => {
            const isCompliant = step.requiredFields.tests!.every(field => {
                const value = (test as any)[field];
                if (Array.isArray(value)) return value.length > 0;
                return value !== undefined && value !== null && value !== '';
            });
            if (isCompliant) compliantCount++;
        });
    }

    const compliance = itemCount > 0 ? Math.round((compliantCount / itemCount) * 100) : 100;

    return {
        stepNumber: step.stepNumber,
        stepName: step.name,
        compliance,
        missingFields,
        itemCount,
        compliantCount,
    };
};

/**
 * Identify critical issues that need immediate attention
 */
const identifyCriticalIssues = (
    data: DfrData,
    stepCompliance: ReturnType<typeof analyzeStep>[]
): ComplianceMetrics['criticalIssues'] => {
    const issues: ComplianceMetrics['criticalIssues'] = [];

    // Critical: No P-Diagrams
    if (data.pDiagramList.length === 0) {
        issues.push({
            severity: 'critical',
            message: 'No P-Diagrams defined. Structure Analysis (Step 2) cannot proceed.',
            step: 2,
            affectedItems: [],
        });
    }

    // Critical: No FMEA items
    if (data.fmeaItems.length === 0) {
        issues.push({
            severity: 'critical',
            message: 'No FMEA items created. Failure Analysis (Step 4) not started.',
            step: 4,
            affectedItems: [],
        });
    }

    // High: FMEA items without S-O-D ratings
    const itemsWithoutRatings = data.fmeaItems.filter(
        item => !item.S || !item.O || !item.D
    );
    if (itemsWithoutRatings.length > 0) {
        issues.push({
            severity: 'high',
            message: `${itemsWithoutRatings.length} FMEA items missing S-O-D ratings`,
            step: 5,
            affectedItems: itemsWithoutRatings.map(i => i.id).slice(0, 5),
        });
    }

    // High: High Risk items without controls (RPN >= 100 or AP === 'H')
    const standard = data.projectMetadata?.fmeaStandard || 'AIAG_VDA';
    const highRiskWithoutControls = data.fmeaItems.filter(item => {
        const isHighRisk = standard === 'AIAG_VDA'
            ? calculateActionPriority(item.S, item.O, item.D) === 'H'
            : (item.RPN || calculateRPN(item.S, item.O, item.D)) >= 100;

        return isHighRisk &&
            (!item.preventionControls || item.preventionControls.length === 0) &&
            (!item.detectionControls || item.detectionControls.length === 0);
    });

    if (highRiskWithoutControls.length > 0) {
        issues.push({
            severity: 'high',
            message: `${highRiskWithoutControls.length} high-risk items (${standard === 'AIAG_VDA' ? 'AP:H' : 'RPN≥100'}) have no controls`,
            step: 6,
            affectedItems: highRiskWithoutControls.map(i => i.id).slice(0, 5),
        });
    }

    // Medium: Low compliance on any step
    stepCompliance.forEach(step => {
        if (step.compliance < 50) {
            issues.push({
                severity: 'medium',
                message: `Step ${step.stepNumber} (${step.stepName}) is only ${step.compliance}% complete`,
                step: step.stepNumber,
                affectedItems: [],
            });
        }
    });

    return issues;
};

/**
 * Generate actionable recommendations based on current state
 */
const generateRecommendations = (
    data: DfrData,
    stepCompliance: ReturnType<typeof analyzeStep>[]
): string[] => {
    const recommendations: string[] = [];

    // Find lowest compliance step
    const lowestStep = stepCompliance.reduce((min, step) =>
        step.compliance < min.compliance ? step : min
    );

    recommendations.push(
        `Focus on Step ${lowestStep.stepNumber}: "${lowestStep.stepName}" (${lowestStep.compliance}% complete)`
    );

    // Specific recommendations based on data state
    if (data.pDiagramList.length === 0) {
        recommendations.push('Start by creating P-Diagrams for your master functions');
    }

    if (data.fmeaItems.length < data.pDiagramList.length * 2) {
        recommendations.push('Each function should have multiple failure modes identified');
    }

    const itemsWithoutTests = data.fmeaItems.filter(item => {
        return !data.dvprTests.some(test =>
            test.traceableRiskId?.includes(item.id)
        );
    });
    if (itemsWithoutTests.length > 0) {
        recommendations.push(`${itemsWithoutTests.length} FMEA items need DVP&R test linkage`);
    }

    const highSeverityItems = data.fmeaItems.filter(item => item.S >= 9);
    if (highSeverityItems.length > 0) {
        recommendations.push(`Review ${highSeverityItems.length} high-severity items (S≥9) for safety implications`);
    }

    return recommendations.slice(0, 5); // Limit to top 5
};

/**
 * Calculate high-level statistics
 */
const calculateStats = (data: DfrData): ComplianceMetrics['stats'] => {
    const standard = data.projectMetadata?.fmeaStandard || 'AIAG_VDA';
    const highRiskCount = data.fmeaItems.filter(item => {
        if (standard === 'AIAG_VDA') {
            return calculateActionPriority(item.S, item.O, item.D) === 'H';
        }
        return (item.RPN || calculateRPN(item.S, item.O, item.D)) >= 100;
    }).length;

    const itemsWithoutControls = data.fmeaItems.filter(
        item => (!item.preventionControls || item.preventionControls.length === 0) &&
            (!item.detectionControls || item.detectionControls.length === 0)
    ).length;

    const itemsWithoutTests = data.fmeaItems.filter(item => {
        return !data.dvprTests.some(test => test.traceableRiskId?.includes(item.id));
    }).length;

    const completePDiagrams = data.pDiagramList.filter(pd =>
        pd.systemFunction?.length > 0 &&
        pd.inputSignals?.length > 0 &&
        pd.expectedOutput?.length > 0 &&
        pd.noiseFactors?.pieceToPiece?.length > 0
    ).length;

    return {
        totalFmeaItems: data.fmeaItems.length,
        highRiskCount,
        itemsWithoutControls,
        itemsWithoutTests,
        completePDiagrams,
        totalPDiagrams: data.pDiagramList.length,
    };
};

/**
 * Get next recommended action based on workflow state
 */
export const getNextAction = (metrics: ComplianceMetrics): string => {
    // Priority 1: Critical issues
    const criticalIssue = metrics.criticalIssues.find(i => i.severity === 'critical');
    if (criticalIssue) {
        return criticalIssue.message;
    }

    // Priority 2: Incomplete steps (lowest first)
    const incompleteStep = metrics.stepCompliance
        .filter(s => s.compliance < 100)
        .sort((a, b) => a.stepNumber - b.stepNumber)[0];

    if (incompleteStep) {
        return `Complete Step ${incompleteStep.stepNumber}: ${incompleteStep.stepName} (${incompleteStep.compliance}%)`;
    }

    // All complete!
    return 'Workflow is complete! Consider updating for continuous improvement.';
};
